import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, ArrowRight, CheckCircle, Users, Mail, Sparkles, Building2, UserCheck } from 'lucide-react';
import { LogoIcon } from '../components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CompanyOnboarding() {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState('');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    companySize: '',
    companyLocations: [],
    pcbPaymentMethod: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
    adminPhone: ''
  });

  const companySizes = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-100', label: '51-100 employees' },
    { value: '101-250', label: '101-250 employees' },
    { value: '251-500', label: '251-500 employees' },
    { value: '500+', label: '500+ employees' }
  ];

  const malaysiaStates = [
    'Kuala Lumpur', 'Selangor', 'Johor', 'Penang', 'Perak', 'Kedah', 
    'Kelantan', 'Terengganu', 'Pahang', 'Negeri Sembilan', 'Melaka',
    'Sabah', 'Sarawak', 'Perlis', 'Putrajaya', 'Labuan'
  ];

  const pcbMethods = [
    'Bank Transfer',
    'Online Banking (FPX)',
    'Credit/Debit Card',
    'Cash',
    'Cheque',
    'Other'
  ];

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    if (role === 'employee') {
      return;
    }
    setStep(2);
  };

  const handleNext = () => {
    if (step === 2) {
      if (!formData.companyName || !formData.companySize) {
        toast.error('Please fill in all required fields');
        return;
      }
      setStep(3);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignUp = () => {
    // Store company data in sessionStorage for the callback
    sessionStorage.setItem('google_signup_data', JSON.stringify({
      isSignup: true,
      companyName: formData.companyName,
      companySize: formData.companySize
    }));
    
    // Redirect to Emergent Auth with dynamic redirect URL.
    // Iter88u — Auth service URL is env-driven so production deploys can
    // override if needed (e.g., a different OAuth proxy domain).
    const authServiceUrl = process.env.REACT_APP_AUTH_SERVICE_URL || 'https://auth.emergentagent.com';
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `${authServiceUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const registerResponse = await axios.post(`${API}/auth/register`, {
        email: formData.adminEmail,
        password: formData.adminPassword,
        full_name: formData.adminName,
        role: 'admin'
      });

      localStorage.setItem('hrms_token', registerResponse.data.token);
      localStorage.setItem('hrms_user', JSON.stringify(registerResponse.data.user));

      localStorage.setItem('company_info', JSON.stringify({
        name: formData.companyName,
        size: formData.companySize,
        locations: formData.companyLocations,
        pcbMethod: formData.pcbPaymentMethod
      }));

      toast.success('Company account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationToggle = (location) => {
    if (formData.companyLocations.includes(location)) {
      setFormData({
        ...formData,
        companyLocations: formData.companyLocations.filter(l => l !== location)
      });
    } else {
      setFormData({
        ...formData,
        companyLocations: [...formData.companyLocations, location]
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="w-full max-w-4xl relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <LogoIcon className="h-10 w-10" />
            <div className="flex items-baseline space-x-1">
              <h1 className="text-3xl font-bold text-white">MyHR</h1>
              <h1 className="text-3xl font-bold text-emerald-400">Orbit</h1>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white">Create a company account</h2>
          <p className="text-slate-400 mt-2">Get started with Malaysia's smartest HRMS</p>
        </div>

        {/* Main Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl border border-slate-700/50 p-8" style={{ borderRadius: '32px' }}>
          {step === 1 && (
            <div className="space-y-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-2">Please describe your role:</h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleRoleSelect('owner')}
                  data-testid="owner-role-btn"
                  className={`p-6 border-2 transition-all hover:-translate-y-1 ${
                    selectedRole === 'owner'
                      ? 'border-emerald-500 bg-emerald-500/10'
                      : 'border-slate-600 hover:border-emerald-400 hover:bg-emerald-500/5'
                  }`}
                  style={{ borderRadius: '20px' }}
                >
                  <div className="text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4" style={{ borderRadius: '16px' }}>
                      <Building2 className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-lg font-semibold text-white">Owner / HR / Payroll</div>
                    <p className="text-sm text-slate-400 mt-2">I manage employees and payroll</p>
                  </div>
                </button>

                <button
                  onClick={() => handleRoleSelect('employee')}
                  data-testid="employee-role-btn"
                  className={`p-6 border-2 transition-all hover:-translate-y-1 ${
                    selectedRole === 'employee'
                      ? 'border-blue-500 bg-blue-500/10'
                      : 'border-slate-600 hover:border-blue-400 hover:bg-blue-500/5'
                  }`}
                  style={{ borderRadius: '20px' }}
                >
                  <div className="text-center">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center mx-auto mb-4" style={{ borderRadius: '16px' }}>
                      <UserCheck className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-lg font-semibold text-white">Employee / Manager</div>
                    <p className="text-sm text-slate-400 mt-2">I'm joining an existing team</p>
                  </div>
                </button>
              </div>

              {/* Employee Notice */}
              {selectedRole === 'employee' && (
                <div className="bg-blue-500/10 border border-blue-500/30 p-6 animate-fade-in" style={{ borderRadius: '20px' }}>
                  <div className="flex items-start space-x-4">
                    <AlertCircle className="h-6 w-6 text-blue-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-lg font-semibold text-blue-300 mb-2">Attention!</h4>
                      <p className="text-blue-200 mb-4">
                        Non-admin employees or managers do not need to create an account to join their team.
                      </p>
                      <p className="text-blue-200">
                        Instead, you may <strong className="text-blue-100">contact your HR representative</strong> so that they can send you an invite to the My HR Orbit system.
                      </p>
                      <div className="mt-6 flex items-center space-x-2">
                        <Mail className="h-5 w-5 text-blue-400" />
                        <span className="text-sm text-blue-300">Your HR will send you an invitation email with login details</span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 text-center">
                    <Button
                      onClick={() => navigate('/login')}
                      variant="outline"
                      className="border-blue-500/50 text-blue-300 hover:bg-blue-500/20"
                      style={{ borderRadius: '12px' }}
                    >
                      Already have an account? Log In
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <form className="space-y-6">
              {/* Progress Indicator */}
              <div className="flex items-center justify-center space-x-4 mb-8">
                <div className="flex items-center space-x-2">
                  <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-semibold">
                    1
                  </div>
                  <span className="font-medium text-white">Company Details</span>
                </div>
                <div className="h-0.5 w-16 bg-slate-600" />
                <div className="flex items-center space-x-2">
                  <div className="h-10 w-10 rounded-full bg-slate-700 text-slate-400 flex items-center justify-center font-semibold">
                    2
                  </div>
                  <span className="font-medium text-slate-500">Admin Details</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-300">Company Name <span className="text-red-400">*</span></Label>
                  <Input
                    required
                    data-testid="company-name-input"
                    placeholder="Company Sdn Bhd"
                    value={formData.companyName}
                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                    className="mt-1.5 h-11 bg-slate-900/50 border-slate-600 text-white placeholder-slate-500"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Company Size <span className="text-red-400">*</span></Label>
                  <select
                    required
                    data-testid="company-size-select"
                    value={formData.companySize}
                    onChange={(e) => setFormData({ ...formData, companySize: e.target.value })}
                    className="mt-1.5 w-full h-11 px-3 bg-slate-900/50 border border-slate-600 text-white"
                    style={{ borderRadius: '12px' }}
                  >
                    <option value="" className="bg-slate-800">Select</option>
                    {companySizes.map(size => (
                      <option key={size.value} value={size.value} className="bg-slate-800">{size.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Company Locations <span className="text-red-400">*</span></Label>
                <div className="mt-2 grid grid-cols-4 gap-2 max-h-48 overflow-y-auto p-3 bg-slate-900/50 border border-slate-600" style={{ borderRadius: '12px' }}>
                  {malaysiaStates.map(state => (
                    <label
                      key={state}
                      className="flex items-center space-x-2 cursor-pointer hover:bg-slate-700/50 p-2 rounded-lg"
                    >
                      <input
                        type="checkbox"
                        checked={formData.companyLocations.includes(state)}
                        onChange={() => handleLocationToggle(state)}
                        className="h-4 w-4 rounded border-slate-500 text-emerald-500 bg-slate-800"
                      />
                      <span className="text-sm text-slate-300">{state}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-slate-300">How do you pay PCB today?</Label>
                <select
                  value={formData.pcbPaymentMethod}
                  onChange={(e) => setFormData({ ...formData, pcbPaymentMethod: e.target.value })}
                  className="mt-1.5 w-full h-11 px-3 bg-slate-900/50 border border-slate-600 text-white"
                  style={{ borderRadius: '12px' }}
                >
                  <option value="" className="bg-slate-800">Select payment method</option>
                  {pcbMethods.map(method => (
                    <option key={method} value={method} className="bg-slate-800">{method}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-between pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  style={{ borderRadius: '12px' }}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleNext}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                  style={{ borderRadius: '12px' }}
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </form>
          )}

          {step === 3 && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Progress Indicator */}
              <div className="flex items-center justify-center space-x-4 mb-8">
                <div className="flex items-center space-x-2">
                  <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                  <span className="font-medium text-slate-400">Company Details</span>
                </div>
                <div className="h-0.5 w-16 bg-emerald-500" />
                <div className="flex items-center space-x-2">
                  <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center font-semibold">
                    2
                  </div>
                  <span className="font-medium text-white">Admin Details</span>
                </div>
              </div>

              <div className="bg-slate-900/50 border border-slate-700 p-4 mb-6" style={{ borderRadius: '16px' }}>
                <h4 className="font-semibold text-white mb-2">Company: {formData.companyName}</h4>
                <p className="text-sm text-slate-400">Size: {formData.companySize} • Locations: {formData.companyLocations.length} selected</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-300">Admin Full Name <span className="text-red-400">*</span></Label>
                  <Input
                    required
                    data-testid="admin-name-input"
                    placeholder="John Doe"
                    value={formData.adminName}
                    onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                    className="mt-1.5 h-11 bg-slate-900/50 border-slate-600 text-white placeholder-slate-500"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Admin Email <span className="text-red-400">*</span></Label>
                  <Input
                    required
                    type="email"
                    data-testid="admin-email-input"
                    placeholder="admin@company.com"
                    value={formData.adminEmail}
                    onChange={(e) => setFormData({ ...formData, adminEmail: e.target.value })}
                    className="mt-1.5 h-11 bg-slate-900/50 border-slate-600 text-white placeholder-slate-500"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-300">Password <span className="text-red-400">*</span></Label>
                  <Input
                    required
                    type="password"
                    data-testid="admin-password-input"
                    placeholder="••••••••"
                    value={formData.adminPassword}
                    onChange={(e) => setFormData({ ...formData, adminPassword: e.target.value })}
                    className="mt-1.5 h-11 bg-slate-900/50 border-slate-600 text-white placeholder-slate-500"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Phone Number</Label>
                  <Input
                    type="tel"
                    placeholder="+60123456789"
                    value={formData.adminPhone}
                    onChange={(e) => setFormData({ ...formData, adminPhone: e.target.value })}
                    className="mt-1.5 h-11 bg-slate-900/50 border-slate-600 text-white placeholder-slate-500"
                    style={{ borderRadius: '12px' }}
                  />
                </div>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/30 p-4" style={{ borderRadius: '16px' }}>
                <div className="flex items-start space-x-2">
                  <Sparkles className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-emerald-200">
                    This admin account will have full access to manage employees, payroll, and company settings with AI-powered features.
                  </p>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  style={{ borderRadius: '12px' }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="create-account-btn"
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white min-w-[150px]"
                  style={{ borderRadius: '12px' }}
                >
                  {loading ? 'Creating...' : 'Create Account'}
                </Button>
              </div>

              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-600"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-slate-800/50 text-slate-400">or sign up with</span>
                </div>
              </div>

              {/* Google Sign Up Button */}
              <button
                type="button"
                onClick={handleGoogleSignUp}
                data-testid="google-signup-btn"
                className="w-full h-12 flex items-center justify-center gap-3 bg-white border border-slate-300 rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-all shadow-sm"
                style={{ borderRadius: '12px' }}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Sign up with Google</span>
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-sm text-slate-500">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-emerald-400 hover:text-emerald-300 font-medium"
            >
              Log In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
